"use client";

import { Field, Input, Text } from "@chakra-ui/react";
import type { ReactElement, ReactNode } from "react";

/**
 * A labelled text input with inline validation feedback.
 *
 * Extracted from the two destination forms, which carried byte-identical copies
 * of the label and input style objects. The values below are those copies
 * verbatim — this is a de-duplication, not a redesign, so nothing here should
 * drift from what the forms already rendered.
 *
 * Non-text controls (the country select, the consent checkbox) deliberately stay
 * out: they need different markup and different a11y wiring, and folding them in
 * would produce one component with several unrelated modes. Compose them beside
 * this one using `labelStyles` where a matching label is needed.
 */
interface FormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Renders below the input and marks it invalid for assistive tech. */
  error?: string;
  placeholder?: string;
  type?: "text" | "email";
  autoComplete?: string;
  inputMode?: "text" | "email";
  /** Rendered between the input and any error, for per-field guidance. */
  hint?: ReactNode;
}

export function FormField({
  label,
  value,
  onChange,
  error,
  placeholder,
  type = "text",
  autoComplete,
  inputMode,
  hint,
}: FormFieldProps): ReactElement {
  return (
    <Field.Root>
      <Field.Label {...labelStyles}>{label}</Field.Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={Boolean(error)}
        {...inputStyles}
      />
      {hint}
      {error && (
        <Text {...errorStyles} role="alert">
          {error}
        </Text>
      )}
    </Field.Root>
  );
}

/** Exported for controls that need a matching label but not a text input. */
export const labelStyles = {
  fontSize: "12px",
  fontWeight: "600",
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: "var(--color-text-primary)",
  mb: 2,
};

export const errorStyles = {
  mt: 2,
  fontSize: "12px",
  color: "var(--color-error)",
};

const inputStyles = {
  h: "44px",
  px: "14px",
  bg: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-md)",
  fontSize: "15px",
  _hover: { borderColor: "var(--color-text-tertiary)" },
  _focus: {
    borderColor: "var(--color-primary)",
    boxShadow: "var(--shadow-focus)",
  },
  _placeholder: { color: "var(--color-text-muted)", fontSize: "14px" },
};
